import React, { useState } from 'react'
import MappingSelect from '../../Components/MappingSelect'
import MappingCheckbox from '../../Components/MappingCheckbox'
import Shared from '../../mappingUi.module.css'
import Style from '../methodMapping.module.css'

const EMPTY_DRAFT = {
    erpMethod: '',
    ecommerceMethod: '',
    isEcommerceDefault: false,
    isErpDefault: false
}

/**
 * The "add a mapping" row. Owns only its own draft; a completed pair is handed to
 * the parent through onAdd and the draft resets.
 */
const MethodMappingForm = ({ idPrefix, entityLabel, erpMethods, ecommerceMethods, isDisabled, isDuplicate, onAdd }) => {
    const [draft, setDraft] = useState(EMPTY_DRAFT)
    const [error, setError] = useState('')
    // Bumped after every add so both pickers remount and drop any custom-entry mode.
    const [formKey, setFormKey] = useState(0)

    const change = (changes) => {
        setDraft((current) => ({ ...current, ...changes }))
        setError('')
    }

    const handleAdd = () => {
        if (draft.erpMethod === '' && draft.ecommerceMethod === '') {
            setError(`Select an ERP and an eCommerce ${entityLabel} to map.`)
            return
        }
        if (draft.erpMethod === '') {
            setError(`Select an ERP ${entityLabel}.`)
            return
        }
        if (draft.ecommerceMethod === '') {
            setError(`Select an eCommerce ${entityLabel}.`)
            return
        }
        if (isDuplicate(draft.erpMethod, draft.ecommerceMethod)) {
            setError(`"${draft.erpMethod}" is already mapped to "${draft.ecommerceMethod}".`)
            return
        }

        onAdd(draft)
        setDraft(EMPTY_DRAFT)
        setError('')
        setFormKey((current) => current + 1)
    }

    return (
        <div className={Shared.formPanel}>
            <div className={`${Shared.grid} ${Style.grid}`}>
                <label className={Shared.fieldLabel} htmlFor={`${idPrefix}ErpMethod`}>ERP Method</label>
                <span />
                <label className={Shared.fieldLabel} htmlFor={`${idPrefix}EcommerceMethod`}>eCommerce Method</label>
                <span className={Shared.fieldLabel}>ERP-Ecom</span>
                <span className={Shared.fieldLabel}>Ecom-ERP</span>
                <span />

                <MappingSelect
                    key={`erp-${formKey}`}
                    inputId={`${idPrefix}ErpMethod`}
                    options={erpMethods}
                    value={draft.erpMethod}
                    placeholder="Select ERP Method"
                    customLabel="Custom ERP Method"
                    customPlaceholder="Enter ERP method"
                    hasError={error !== '' && draft.erpMethod === ''}
                    isDisabled={isDisabled}
                    onChange={(value) => change({ erpMethod: value })}
                />
                <span className={Shared.arrow} aria-hidden="true">&rarr;</span>
                <MappingSelect
                    key={`ecommerce-${formKey}`}
                    inputId={`${idPrefix}EcommerceMethod`}
                    options={ecommerceMethods}
                    value={draft.ecommerceMethod}
                    placeholder="Select eCommerce Method"
                    customLabel="Custom eCommerce Method"
                    customPlaceholder="Enter eCommerce method"
                    hasError={error !== '' && draft.ecommerceMethod === ''}
                    isDisabled={isDisabled}
                    onChange={(value) => change({ ecommerceMethod: value })}
                />
                <MappingCheckbox
                    inputId={`${idPrefix}NewIsEcommerceDefault`}
                    label="ERP-Ecom"
                    checked={draft.isEcommerceDefault}
                    isDisabled={isDisabled}
                    onChange={(checked) => change({ isEcommerceDefault: checked })}
                />
                <MappingCheckbox
                    inputId={`${idPrefix}NewIsErpDefault`}
                    label="Ecom-ERP"
                    checked={draft.isErpDefault}
                    isDisabled={isDisabled}
                    onChange={(checked) => change({ isErpDefault: checked })}
                />
                <button
                    type="button"
                    className={Shared.addButton}
                    title="Add mapping"
                    aria-label="Add mapping"
                    disabled={isDisabled}
                    onClick={handleAdd}
                >
                    <i className="uil-plus" aria-hidden="true" />
                </button>
            </div>
            {error ? <p className={Shared.formError} role="alert">{error}</p> : null}
        </div>
    )
}

export default MethodMappingForm
